import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Clock, Smartphone, CreditCard, 
  HelpCircle, ChevronDown, ChevronUp, Truck, FileSpreadsheet 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const faqCategories = [
  {
    title: "البدء والتسجيل",
    icon: HelpCircle,
    questions: [
      {
        q: "كيف أبدأ في استخدام نظام Target Express كتاجر؟",
        a: "الأمر بسيط! قم بإنشاء حساب جديد، أكمل بيانات نشاطك التجاري، ومن ثم يمكنك البدء فوراً في إضافة شحناتك يدوياً، أو عبر رفع ملف Excel، أو من خلال الربط المباشر (API) مع متجرك الإلكتروني."
      },
      {
        q: "هل هناك رسوم اشتراك شهري إلزامية؟",
        a: "نقدم خططاً مرنة تناسب جميع أحجام الأعمال. لدينا خطط تدفع حسب الاستخدام، وخطط مخصصة للشركات ذات أحجام الشحن الكبيرة. يمكنك مراجعة صفحة 'الأسعار' للتفاصيل."
      }
    ]
  },
  {
    title: "الشحن والتتبع",
    icon: Truck,
    questions: [
      {
        q: "كيف يمكنني رفع شحناتي بسرعة؟",
        a: "نوفر لك 3 طرق مرنة: 1) الإدخال اليدوي عبر لوحة التحكم. 2) رفع ملف Excel جاهز بصيغتنا المعتمدة. 3) الربط المباشر (API) لمتاجرك الإلكترونية لأتمتة العملية بالكامل."
      },
      {
        q: "هل يمكنني وعملائي تتبع حالة الشحنة؟",
        a: "نعم، نوفر نظام تتبع لحظي (Real-time). يحصل العميل على رابط تتبع مخصص، ويمكنك أنت متابعة كل شحنة خطوة بخطوة من لحظة الاستلام حتى التسليم النهائي."
      },
      {
        q: "ماذا يحدث في حالة رفض العميل استلام الشحنة (مرتجع)؟",
        a: "يقوم المندوب بتحديث الحالة فوراً إلى 'مرتجع'. ستظهر الشحنة في قسم 'المرتجعات' بلوحة تحكمك، ونقوم بإعادتها لمخازنك أو لفرعك الأقرب حسب سياسة الاتفاق، دون أي تعقيدات."
      }
    ]
  },
  {
    title: "التحصيل المالي (COD)",
    icon: CreditCard,
    questions: [
      {
        q: "متى يتم تحويل أموال التحصيل (COD) إلى حسابي؟",
        a: "نلتزم بدورات تحويل منتظمة وسريعة (أسبوعية أو حسب الاتفاق المبرم). يمكنك متابعة كل جنيه من خلال قسم 'التقارير المالية' و 'المقبوضات' في لوحة التحكم بدقة وشفافية تامة."
      },
      {
        q: "هل أتمكن من استخراج فواتير وتقارير مالية؟",
        a: "بالتأكيد. يوفر النظام تقارير مالية شاملة وقابلة للتصدير (Excel/PDF) توضح إجمالي الشحنات، المحصلة، المرتجعة، والعمولات المستحقة بدقة."
      }
    ]
  },
  {
    title: "الميزات التقنية والدعم",
    icon: Smartphone,
    questions: [
      {
        q: "هل يتم إشعار عملائي بحالة الشحنة تلقائياً؟",
        a: "نعم، نظامنا متكامل مع WhatsApp و SMS. يتم إرسال رسائل آلية للعميل عند: استلام الشحنة، خروجها للتوصيل، وعند اكتمال التسليم، مما يقلل مكالمات الاستفسار ويزيد رضا العملاء."
      },
      {
        q: "كيف يمكنني التواصل مع الدعم الفني؟",
        a: "فريق الدعم جاهز لمساعدتك. يمكنك فتح 'تذكرة دعم' مباشرة من لوحة التحكم، أو التواصل معنا عبر صفحة 'اتصل بنا' أو عبر قنوات الواتساب المخصصة للتجار."
      }
    ]
  }
];

export default function FaqPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleQuestion = (index: string) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Header Section */}
      <div className="bg-slate-950 text-white py-16 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 opacity-90"></div>
        <div className="relative max-w-3xl mx-auto">

          <div className="flex justify-start mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-white/30 bg-white/10 text-white hover:bg-white hover:text-slate-900 gap-2"
            >
              العودة للرئيسية
            </Button>
          </div>

          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4">الأسئلة الشائعة</h1>
            <p className="text-slate-300 text-lg">
              كل ما تحتاج معرفته عن خدمات الشحن، التتبع، والتحصيل المالي في Target Express.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 -mt-8 relative z-10">
        <div className="grid gap-8">
          {faqCategories.map((category, catIdx) => {
            const Icon = category.icon;
            return (
              <div key={catIdx}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">{category.title}</h2>
                </div>
                
                <div className="space-y-3">
                  {category.questions.map((item, qIdx) => {
                    const globalIndex = `${catIdx}-${qIdx}`;
                    const isOpen = openIndex === globalIndex;
                    
                    return (
                      <Card key={qIdx} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                        <button
                          onClick={() => toggleQuestion(globalIndex)}
                          className="w-full flex items-center justify-between p-5 text-right bg-white hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-semibold text-slate-800 text-base md:text-lg">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-orange-600 flex-shrink-0 mr-4" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mr-4" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <CardContent className="p-5 pt-0 bg-white border-t border-slate-100">
                            <p className="text-slate-600 leading-relaxed">{item.a}</p>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-slate-950 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-2xl font-bold mb-3">لم تجد إجابة لسؤالك؟</h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              فريق الدعم الخاص بنا جاهز لمساعدتك على مدار الساعة. لا تتردد في التواصل معنا.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 h-auto text-lg"
                onClick={() => navigate('/contact')}
              >
                تواصل مع الدعم الفني
              </Button>
              <Button 
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 h-auto text-lg"
                onClick={() => navigate('/tickets')}
              >
                فتح تذكرة دعم جديدة
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
